import { LeadStatus } from "@/components/simran/LeadStatus";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, Search, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtINR } from "@/lib/api";
import { OrderStatusDialog } from "@/components/OrderStatusDialog";
import { ProgressDialog } from "@/components/ProgressDialog";
import { AddTeacherDialog, DeleteTeacherButton } from "@/components/TeacherAdmin";
import { AddSchoolDialog, SchoolActions, AnnounceChampionButton } from "@/components/SchoolReports";
import { usePaymentConfig } from "@/components/PaymentMethod";
import { ShowcaseThumbs, ShowcaseReview, SpotlightDialog } from "@/components/ShowcaseAdmin";
import { StatusPill } from "@/components/ShowcaseUpload";
import { AdmissionPill, AdmissionReview, AddExamDialog, DeleteExamButton } from "@/components/AdmissionsAdmin";
import { AiotAdmin } from "@/components/aiot/AiotAdmin";
import { AiTag, aiTag } from "@/components/AiTag";
import { ReceiptActions } from "@/components/admin/ReceiptActions";
import { RevenueDashboard } from "@/components/admin/RevenueDashboard";
import { CouponsAdmin, AuditLog, CredentialsButton } from "@/components/admin/CouponsAdmin";
import { SchoolOnboardingAdmin } from "@/components/admin/SchoolOnboardingAdmin";
import { ReferralsAdmin } from "@/components/admin/ReferralsAdmin";
import { RoboxAdmin } from "@/components/admin/RoboxAdmin";
import { SwetaInbox } from "@/components/admin/SwetaInbox";

const TABS = {
  revenue: { label: "Revenue", cols: [] },
  coupons: { label: "Coupons", cols: [] },
  onboarding: { label: "School Onboarding", cols: [] },
  referrals: { label: "Referrals", cols: [] },
  robox: { label: "Robo-X", cols: [] },
  sweta: { label: "Sweta Inbox", cols: [] },
  audit: { label: "Audit Log", cols: [] },
  aiot: { label: "AIoT Diploma", cols: [] },
  simran: {
    label: "Simran Inbox",
    cols: [["created_at", "Asked"], ["ai_urgency", "Urgency"], ["ai_category", "Type"], ["lead_actions", "Status"], ["question", "Question"], ["name", "Name"], ["contact", "Mobile / email"], ["page", "Page"], ["answer", "Simran said"]],
  },
  enrollments: {
    label: "Enrollments",
    cols: [["created_at", "Date"], ["receipt", "Receipt"], ["receipt_actions", "PDF"], ["credentials_actions", "Login"], ["student_name", "Student"], ["progress_action", "Progress"], ["parent_name", "Parent"], ["class_grade", "Class"], ["program", "Program"], ["plan", "Plan"], ["amount_inr", "Amount"], ["coupon_code", "Coupon"], ["payment_method", "Method"], ["payment_status", "Payment"], ["phone", "Phone"], ["email", "Email"], ["school_name", "School"], ["city", "City"], ["state", "State"]],
  },
  orders: {
    label: "Orders",
    cols: [["created_at", "Date"], ["receipt", "Order no."], ["receipt_actions", "PDF"], ["customer_name", "Customer"], ["customer_email", "Email"], ["customer_phone", "Phone"], ["items", "Items"], ["total_inr", "Total"], ["payment_method", "Method"], ["payment_status", "Payment"], ["fulfilment_status", "Fulfilment"], ["tracking_number", "Tracking"], ["actions", "Action"], ["shipping_address", "Ship to"]],
  },
  partnerships: {
    label: "School Requests",
    cols: [["created_at", "Date"], ["ai_spam", "Spam check"], ["ai_topic", "Topic"], ["school_name", "School"], ["contact_person", "Contact"], ["designation", "Designation"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["state", "State"], ["student_count", "Students"], ["message", "Message"]],
  },
  contacts: {
    label: "Contact Messages",
    cols: [["created_at", "Date"], ["ai_spam", "Spam check"], ["ai_topic", "Topic"], ["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["organization", "Organisation"], ["message", "Message"]],
  },
  teachers: {
    label: "Trainers",
    cols: [["created_at", "Added"], ["name", "Name"], ["email", "Login email"], ["school_name", "School"], ["teacher_actions", "Action"]],
  },
  schools: {
    label: "School Reports",
    cols: [["school_name", "School"], ["principal_name", "Principal"], ["email", "Report email"], ["has_login", "Login"], ["school_actions", "Action"]],
  },
  showcase: {
    label: "Project Photos",
    cols: [["created_at", "Uploaded"], ["photos", "Photos"], ["title", "Project"], ["student_name", "Student"], ["ai_caption", "Caption check"], ["status", "Status"], ["showcase_actions", "Action"], ["school_name", "School"], ["program", "Program"], ["uploaded_by", "Trainer"], ["description", "Story"]],
  },
  champions: {
    label: "Champions",
    cols: [["month", "Month"], ["name", "Champion"], ["school_name", "School"], ["projects", "Projects"], ["earnings_inr", "Earnings"], ["spotlight_story", "Spotlight story"], ["spotlight_actions", "Action"]],
  },
  admissions: {
    label: "Admissions",
    cols: [["submitted_at", "Submitted"], ["student_name", "Student"], ["admission_status", "Status"], ["admission_actions", "Action"], ["class_grade", "Class"], ["program", "Program"], ["aadhaar_masked", "Aadhaar"], ["school_name", "School"], ["city", "City"], ["parent_phone", "Parent mobile"], ["student_code", "Student ID"], ["roll_no", "Roll no."], ["email", "Email"], ["email_verified", "Email verified"]],
  },
  doubts: {
    label: "Doubts",
    cols: [["created_at", "Asked"], ["status", "Status"], ["question", "Question"], ["flagged", "Flagged by Zoya"], ["name", "Student"], ["school_name", "School"], ["email", "Email"], ["phone", "Phone"], ["lesson", "Lesson"], ["claimed_by_name", "Trainer"], ["reply_count", "Replies"], ["resolution_note", "Note"]],
  },
  exams: {
    label: "Exams",
    cols: [["date", "Date"], ["start_time", "Time"], ["title", "Exam"], ["program", "Program"], ["mode", "Mode"], ["center", "Centre"], ["duration_min", "Minutes"], ["exam_actions", "Action"]],
  },
};

const ACTION_COLS = ["receipt_actions", "lead_actions", "actions", "progress_action", "teacher_actions", "school_actions", "showcase_actions", "spotlight_actions", "photos", "admission_actions", "exam_actions"];

const cell = (row, key) => {
  const v = row[key];
  if (key === "items") return (v || []).map((i) => `${i.name} x${i.qty}`).join("; ");
  if (key === "created_at" || key === "submitted_at") return v ? new Date(v).toLocaleString("en-IN") : "";
  if (key === "admission_status") return row.status;
  if (key === "amount_inr" || key === "total_inr" || key === "earnings_inr") return fmtINR(v);
  if (key === "has_login") return v ? "Yes" : "No";
  if (key === "email_verified") return v ? "Yes" : "Not yet";
  if (key === "flagged") return v ? "Yes" : "";
  if (key === "fulfilment_status") return v || "new";
  if (key === "receipt") return (row.id || "").slice(0, 8).toUpperCase();
  if (key.startsWith("ai_")) return aiTag(row.ai, key.slice(3))[0];
  if (ACTION_COLS.includes(key)) return "";
  return v ?? "";
};

const toCSV = (rows, cols) => {
  const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
  return [cols.map((c) => esc(c[1])).join(","), ...rows.map((r) => cols.map((c) => esc(cell(r, c[0]))).join(","))].join("\n");
};

const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-2xl font-extrabold text-[#0A192F] font-['Outfit'] mt-1">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const cfg = usePaymentConfig();
  const [tab, setTab] = useState("enrollments");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("rs_admin_token")}` } }), []);
  const onErr = useCallback((e) => { if (e.response?.status === 401) { localStorage.removeItem("rs_admin_token"); nav("/admin/login"); } }, [nav]);

  const current = useRef(tab);
  current.current = tab;
  const load = useCallback(() => {
    api.get("/admin/summary", auth).then((r) => setSummary(r.data)).catch(onErr);
    if (["aiot", "revenue", "coupons", "onboarding", "referrals", "robox", "sweta", "audit"].includes(tab)) return;
    api.get(`/admin/${tab}`, auth).then((r) => { if (current.current === tab) setRows(r.data.items); }).catch(onErr);
  }, [tab, auth, onErr]);

  useEffect(() => {
    if (!localStorage.getItem("rs_admin_token")) { nav("/admin/login"); return; }
    setRows([]);
    load();
  }, [load, nav]);

  const cols = TABS[tab].cols;
  const hasPayment = tab === "enrollments" || tab === "orders";
  const filtered = rows.filter((r) =>
    (!hasPayment || status === "all" || r.payment_status === status) &&
    (!q || JSON.stringify(r).toLowerCase().includes(q.toLowerCase())));

  const toCSVCols = cols.filter((c) => !ACTION_COLS.includes(c[0]));
  const exportCSV = () => {
    const blob = new Blob([toCSV(filtered, toCSVCols)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `robospark-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const logout = () => { localStorage.removeItem("rs_admin_token"); api.post("/auth/logout"); nav("/admin/login"); };

  return (
    <div className="bg-[#E8F0FE] min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <h1 className="text-3xl font-extrabold text-[#0A192F]">Admin Dashboard</h1>
          <div className="flex gap-2 items-center flex-wrap">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.whatsapp_enabled ? "bg-emerald-500/15 text-emerald-700" : "bg-slate-900/[0.07] text-slate-600"}`} data-testid="admin-whatsapp-status">WhatsApp: {cfg.whatsapp_enabled ? "On" : "Off (add Twilio keys)"}</span>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.razorpay_enabled ? "bg-emerald-500/15 text-emerald-700" : "bg-slate-900/[0.07] text-slate-600"}`} data-testid="admin-razorpay-status">Razorpay: {cfg.razorpay_enabled ? "On" : "Off"}</span>
            <AnnounceChampionButton auth={auth} />
            <Button variant="outline" onClick={load} data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-1" />Logout</Button>
          </div>
        </div>
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
            <Stat label="Revenue (paid)" value={fmtINR(summary.revenue_inr)} testid="stat-revenue" />
            <Stat label="Enrollments" value={`${summary.paid_enrollments}/${summary.enrollments}`} testid="stat-enrollments" />
            <Stat label="Orders" value={`${summary.paid_orders}/${summary.orders}`} testid="stat-orders" />
            <Stat label="School requests" value={summary.partnerships} testid="stat-partnerships" />
            <Stat label="Messages" value={summary.contacts} testid="stat-contacts" />
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-8">
          {Object.entries(TABS).map(([k, t]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${tab === k ? "bg-[#FF3B3B] text-white shadow-[0_8px_24px_-8px_rgba(255,59,59,0.7)]" : "bg-white border border-slate-200 text-[#0A192F]"}`}>{t.label}</button>
          ))}
        </div>
        {tab === "revenue" ? <RevenueDashboard auth={auth} onErr={onErr} /> : tab === "coupons" ? <CouponsAdmin auth={auth} onErr={onErr} /> : tab === "onboarding" ? <SchoolOnboardingAdmin auth={auth} onErr={onErr} /> : tab === "audit" ? <AuditLog auth={auth} onErr={onErr} /> : tab === "referrals" ? <ReferralsAdmin auth={auth} onErr={onErr} /> : tab === "robox" ? <RoboxAdmin auth={auth} onErr={onErr} /> : tab === "sweta" ? <SwetaInbox auth={auth} onErr={onErr} /> : tab === "aiot" ? <AiotAdmin auth={auth} /> : (<>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 bg-white" data-testid="admin-search" />
          </div>
          {hasPayment && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="admin-status-filter">
              <option value="all">All payments</option><option value="paid">Paid</option><option value="pending">Pending</option>
            </select>
          )}
          <span className="text-sm text-slate-500" data-testid="admin-row-count">{filtered.length} records</span>
          {tab === "teachers" && <AddTeacherDialog auth={auth} onDone={load} />}
          {tab === "schools" && <AddSchoolDialog auth={auth} onDone={load} />}
          {tab === "exams" && <AddExamDialog auth={auth} onDone={load} />}
          <Button onClick={exportCSV} className="rs-cta rounded-full sm:ml-auto" data-testid="admin-export-btn"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
        </div>
        <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-table">
            <thead className="bg-white text-left">
              <tr>{cols.map((c) => <th key={c[0]} className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap">{c[1]}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-200" data-testid={`admin-row-${r.id}`}>
                  {cols.map((c) => (
                    <td key={c[0]} className="px-3 py-2 whitespace-nowrap max-w-xs truncate">
                      {c[0] === "payment_status"
                        ? <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`}>{r.payment_status}</span>
                        : c[0] === "receipt_actions" ? <ReceiptActions kind={tab === "orders" ? "order" : "enrollment"} row={r} auth={auth} />
                        : c[0] === "credentials_actions" ? <CredentialsButton row={r} auth={auth} />
                        : c[0] === "lead_actions" ? <LeadStatus lead={r} auth={auth} onDone={load} />
                        : c[0] === "actions" ? <OrderStatusDialog order={r} auth={auth} onDone={load} />
                        : c[0] === "teacher_actions" ? <DeleteTeacherButton teacher={r} auth={auth} onDone={load} />
                        : c[0] === "school_actions" ? <SchoolActions school={r} auth={auth} onDone={load} />
                        : c[0] === "photos" ? <ShowcaseThumbs item={r} />
                        : c[0] === "status" ? <StatusPill s={r.status} testid={`showcase-status-${r.id}`} />
                        : c[0] === "showcase_actions" ? <ShowcaseReview item={r} auth={auth} onDone={load} />
                        : c[0] === "spotlight_actions" ? <SpotlightDialog key={r.spotlight_updated_at || r.id} champ={r} auth={auth} onDone={load} />
                        : c[0] === "progress_action" ? <ProgressDialog key={r.progress?.updated_at || r.id} enrollment={r} auth={auth} onDone={load} />
                        : c[0] === "admission_status" ? <AdmissionPill s={r.status} id={r.id} />
                        : c[0] === "admission_actions" ? <AdmissionReview key={r.status} item={r} auth={auth} onDone={load} />
                        : c[0] === "exam_actions" ? <DeleteExamButton exam={r} auth={auth} onDone={load} />
                        : c[0] === "fulfilment_status" ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-white text-[#0A192F] capitalize" data-testid={`fulfilment-${r.id}`}>{cell(r, c[0])}</span>
                        : c[0].startsWith("ai_") ? <AiTag ai={r.ai} kind={c[0].slice(3)} testid={`${c[0].replace("_", "-")}-${r.id}`} />
                        : cell(r, c[0])}
                    </td>
                  ))}
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-slate-500">No records</td></tr>}
            </tbody>
          </table>
        </div>
        </>)}
      </div>
    </div>
  );
}
